import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于开云棋牌',
  description: '开云棋牌是集斗地主、麻将、德州扑克等经典棋牌游戏于一体的正版平台，品牌介绍、信誉资质与联系方式。',
  isPartOf: {
    '@type': 'WebSite',
    name: '开云棋牌',
    url: 'https://kaiyunqipai.com',
  },
  about: {
    '@type': 'Organization',
    name: '开云棋牌',
    description: '开云棋牌成立于2019年，专注于在线棋牌游戏研发与运营，累计服务超过5000万注册用户。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-8888',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  },
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 开云棋牌 | 品牌介绍、信誉资质与联系方式';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rg01vnahb7382ovlv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormStatus('error');
        setFormError('提交失败，请稍后重试');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <JsonLd data={aboutSchema} />
        <nav className="bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <ol className="flex items-center gap-1.5 py-3 text-xs md:text-sm flex-wrap">
              <li>
                <Link to="/" className="text-foreground-400 hover:text-primary-500 transition-colors">
                  首页
                </Link>
              </li>
              <li className="text-foreground-300">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="text-foreground-700 font-medium">关于我们</li>
            </ol>
          </div>
        </nav>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 mb-4">
                <i className="ri-building-4-line text-primary-500 text-sm"></i>
                <span className="text-primary-600 text-xs font-medium">关于我们</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-3">
                关于<span className="text-primary-600">开云棋牌</span>
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
                了解我们的品牌故事、核心价值与发展历程
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center mb-14 md:mb-20">
              <div>
                <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">开云棋牌</strong>成立于2019年，是一家专注于在线棋牌游戏研发与运营的科技企业。平台汇集了斗地主、麻将、德州扑克、百家乐、牛牛、龙虎斗等多种国内外经典棋牌游戏，致力于为全球华人玩家提供安全、公平、优质的棋牌娱乐体验。
                  </p>
                  <p>
                    开云棋牌始终坚持"<strong className="text-foreground-900">公平公正、用户至上</strong>"的经营理念，采用国际认证的随机数生成算法（RNG），确保每一局游戏结果的随机性和不可预测性。平台所有游戏均获得正版授权，合法合规运营。
                  </p>
                  <p>
                    经过多年的发展，开云棋牌已累计服务超过<strong className="text-primary-600">5000万</strong>注册用户，日活跃玩家突破200万，成为行业领先的棋牌游戏平台。平台支持PC、iOS、Android等多端互通，让玩家随时随地畅享棋牌乐趣。
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden h-72 md:h-80 lg:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxurious%20modern%20office%20space%20with%20warm%20ambient%20lighting%2C%20dark%20wood%20and%20golden%20accents%2C%20elegant%20corporate%20headquarters%2C%20professional%20atmosphere%2C%20sophisticated%20minimal%20design%2C%20warm%20crimson%20and%20gold%20tones&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="开云棋牌品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="mb-14 md:mb-20">
              <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-8 text-center">
                信誉<span className="text-primary-600">资质</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {[
                  { icon: 'ri-verified-badge-line', title: '正版授权', desc: '所有游戏均获正规授权，合法合规运营，保障玩家权益' },
                  { icon: 'ri-medal-line', title: 'RNG认证', desc: '采用国际认证随机数算法，通过第三方权威机构公平性审计' },
                  { icon: 'ri-shield-check-line', title: '安全认证', desc: '银行级数据加密技术，通过ISO27001信息安全体系认证' },
                  { icon: 'ri-award-line', title: '行业荣誉', desc: '连续三年荣获"最受玩家信赖棋牌平台"称号，行业高度认可' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-background-100 rounded-xl border border-background-200 p-5 md:p-6 text-center"
                  >
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-accent-50 flex items-center justify-center mb-4">
                      <i className={`${item.icon} text-accent-500 text-2xl`}></i>
                    </div>
                    <h4 className="font-bold text-foreground-900 mb-2">{item.title}</h4>
                    <p className="text-foreground-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div>
                <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
                  联系我们
                </h2>
                <div className="space-y-4">
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们的客服团队将竭诚为您服务。
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-primary-500"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">客服热线</div>
                        <div className="font-semibold text-foreground-900">400-888-8888</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-500"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">电子邮箱</div>
                        <div className="font-semibold text-foreground-900">service@kaiyunqipai.com</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-500"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">公司地址</div>
                        <div className="font-semibold text-foreground-900">中国·北京</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-primary-500"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">服务时间</div>
                        <div className="font-semibold text-foreground-900">7×24小时全天候在线</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-100 rounded-xl border border-background-200 p-5 md:p-6">
                  <h3 className="text-lg font-bold text-foreground-900 font-heading mb-4">
                    在线留言
                  </h3>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    id="about-contact-form"
                    className="space-y-4"
                  >
                    <div>
                      <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact_name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact_email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact_subject"
                        name="subject"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder="请输入留言主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact_message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="hp-textarea"
                    />

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中...
                        </span>
                      ) : (
                        '提交留言'
                      )}
                    </button>

                    {formStatus === 'success' && (
                      <div className="p-3 rounded-lg bg-green-50 text-green-700 text-sm text-center">
                        <i className="ri-checkbox-circle-line mr-1.5"></i>
                        留言提交成功！我们会尽快与您联系。
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm text-center">
                        <i className="ri-error-warning-line mr-1.5"></i>
                        {formError}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}